/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core'
import { AbstractControl, UntypedFormGroup, Validators } from '@angular/forms'
import { Subscription } from 'rxjs'
import { BaseFormControlComponent } from '../base-form-control.component'
import { DynamicFormLayout, DynamicFormLayoutService } from '../../../service/dynamic-form-layout.service'
import { DynamicFormControlLayout } from '../../../model/misc/dynamic-form-control-layout.model'
import { DynamicCheckboxModel } from '../../../model/checkbox/dynamic-checkbox.model'
import { DynamicFormValidationService } from '../../../service/dynamic-form-validation.service'
import { DynamicFormComponentService } from '../../../service/dynamic-form-component.service'

export const SELECT_ALL_CHECKBOX_ID = 'select_all'

@Component({
  selector: 'dynamic-basic-checkbox',
  templateUrl: './dynamic-basic-checkbox.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DynamicBasicCheckboxComponent extends BaseFormControlComponent implements OnInit, OnDestroy {
  declare layoutService: DynamicFormLayoutService
  declare validationService: DynamicFormValidationService
  private componentService: DynamicFormComponentService

  private _selectAllSubscriptions: Subscription[] = []

  get controlState(): 'error' | 'none' {
    return this.isInvalid && this.control.touched ? 'error' : 'none'
  }
  @Input() formLayout: DynamicFormLayout = {}
  @Input() group: UntypedFormGroup = new UntypedFormGroup({})
  @Input() layout: DynamicFormControlLayout = {}
  @Input() model: DynamicCheckboxModel = new DynamicCheckboxModel({ id: '', label: '' })

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur: EventEmitter<any> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() change: EventEmitter<any> = new EventEmitter()
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() focus: EventEmitter<any> = new EventEmitter()

  public ngOnInit(): void {
    const control = this.group?.get(this.model?.id)

    if (control && this.model?.required) {
      control.addValidators(Validators.requiredTrue)
      control.updateValueAndValidity()
    }

    if (this.model?.id === SELECT_ALL_CHECKBOX_ID) {
      // Defer wiring until sibling checkbox components have been registered.
      Promise.resolve().then(() => this.setupSelectAllSync())
    }
  }

  public ngOnDestroy(): void {
    this._selectAllSubscriptions.forEach((subscription) => subscription.unsubscribe())
    this._selectAllSubscriptions = []
  }

  public onChange($event: any) {
    if ($event instanceof Event) {
      $event.stopPropagation()
    }

    const control = this.group.get(this.model.id)

    if (this.model.id === SELECT_ALL_CHECKBOX_ID) {
      this.applySelectAll(control?.value === true)
    } else if (!!this.model.openURLOnClick && control?.value === true) {
      window.open(this.model.openURLOnClick, '_blank')
    }

    this.change.emit($event)
  }

  private setupSelectAllSync(): void {
    const others = this.getOtherCheckboxControls()

    others.forEach((control) => {
      this._selectAllSubscriptions.push(control.valueChanges.subscribe(() => this.syncSelectAllState()))
    })

    this.syncSelectAllState()
  }

  private applySelectAll(checked: boolean): void {
    this.getOtherCheckboxControls().forEach((control) => {
      if (control.value !== checked) {
        control.setValue(checked)
      }
    })
  }

  private syncSelectAllState(): void {
    const selectAllControl = this.group.get(this.model.id)
    const others = this.getOtherCheckboxControls()
    const allChecked = others.length > 0 && others.every((control) => control.value === true)

    if (selectAllControl && selectAllControl.value !== allChecked) {
      // Programmatic update: must not re-trigger the user change handler.
      selectAllControl.setValue(allChecked, { emitEvent: false })
    }
  }

  private getOtherCheckboxControls(): AbstractControl[] {
    return this.componentService
      .getFormControlRefs()
      .map((ref) => ref.instance)
      .filter(
        (instance): instance is DynamicBasicCheckboxComponent =>
          instance instanceof DynamicBasicCheckboxComponent && instance.model?.id !== SELECT_ALL_CHECKBOX_ID,
      )
      .map((instance) => instance.group?.get(instance.model.id))
      .filter((control): control is AbstractControl => !!control)
  }

  constructor() {
    const layoutService = inject(DynamicFormLayoutService)
    const validationService = inject(DynamicFormValidationService)
    const componentService = inject(DynamicFormComponentService)
    super()
    this.layoutService = layoutService
    this.validationService = validationService
    this.componentService = componentService
  }
}
